import * as React from 'react';
import {RouteComponentProps, withRouter} from "react-router";
import {connect, Dispatch} from "react-redux";
import {option, Option} from "ts-option";
import JobAppointmentEvent from "../../../../model/job/event/JobAppointmentEvent";
import {getJobByIdOrRefNumber} from "../../../../store/view/job";
import Appointment from "../../../../model/appointment/Appointment";
import IndividualContact from "../../../../model/contact/IndividualContact";
import {OnSuccess} from "../../../../store/action/actionHelpers";
import ConfirmAppointmentStatusChangeModal from "../../../appointment/ConfirmAppointmentStatusChangeModal";
import ConfirmDeleteAppointmentModal from "../../../appointment/ConfirmDeleteAppointmentModal";
import AppointmentStatus, {AppointmentStatusEnum} from "../../../../model/appointment/AppointmentStatus";
import {RootState} from "../../../../store/reducer/root/RootState";
import {deleteJobAppointment, updateJobAppointment} from "../../../../store/action/appointment/appointment";
import Toast from "../../../helper/Toast";
import JobAppointment from "../../../../model/appointment/job/JobAppointment";
import JobAppointmentStatusEvent from "../../../../model/job/event/JobAppointmentStatusEvent";
import CommonStreamItem, {StreamItemAction} from "../../../../hiberUiKit/src/molecules/StreamItem/CommonStreamItem";
import {getIndividualContact} from "../../../../store/view/contact";
import moment = require("moment");

interface ComponentProps {
	/**
	 * Job activity to display.
	 */
	activity: JobAppointmentEvent | JobAppointmentStatusEvent;
	/**
	 * On edit appointment click
	 * @param {Appointment} appointment
	 */
	onEditAppointmentClick: (appointmentId: string) => void;
	/**
	 * Disabled edit, un/confirm and delete actions
	 */
	readOnly?: boolean;
}

interface ReduxProps {
	/**
	 * Job appointments.
	 */
	appointment: Option<JobAppointment>;
	/**
	 * Appointment job primary contact.
	 */
	contact: Option<IndividualContact>
}

interface DispatchProps {
	/**
	 * Update appointment
	 * @param {string} jobId
	 * @param {Appointment} appointment
	 */
	updateAppointment: (jobId: string, appointment: JobAppointment) => void;
	/**
	 * Delete appointment
	 * @param {string} appointmentId
	 * @param {OnSuccess} onSuccess
	 */
	deleteAppointment: (appointmentId: string, onSuccess: OnSuccess) => void;
}

interface State {
	deleteAppointmentConfirmationVisible: boolean,
	confirmationVisible: boolean
}

type Props = ComponentProps & ReduxProps & DispatchProps & RouteComponentProps<{}>;

class JobActivityAppointmentItem extends React.Component<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			confirmationVisible: false,
			deleteAppointmentConfirmationVisible: false
		};
		this.editAppointmentClick = this.editAppointmentClick.bind(this);
		this.unConfirmAppointmentClick = this.unConfirmAppointmentClick.bind(this);
		this.confirmAppointmentClick = this.confirmAppointmentClick.bind(this);
		this.onModalConfirm = this.onModalConfirm.bind(this);
		this.isConfirmed = this.isConfirmed.bind(this);
		this.isCurrent = this.isCurrent.bind(this);
		this.deleteAppointmentClick = this.deleteAppointmentClick.bind(this);
		this.deleteAppointment = this.deleteAppointment.bind(this);
	}

	render() {
		return (
			this.props.appointment.map(a =>
				<React.Fragment>
					<CommonStreamItem event={this.props.activity} actions={this.getActions()}/>
					{this.state.confirmationVisible &&
					<ConfirmAppointmentStatusChangeModal onClose={() => this.setState({confirmationVisible: false})}
														 onConfirm={this.onModalConfirm}/>}
					{this.state.deleteAppointmentConfirmationVisible &&
					<ConfirmDeleteAppointmentModal
						onClose={() => this.setState({deleteAppointmentConfirmationVisible: false})}
						onConfirm={this.deleteAppointment}/>}
				</React.Fragment>
			).orNull
		)
	}

	getActions(): StreamItemAction[] {
		let actions: StreamItemAction[] = [];
		if (!this.props.readOnly) {
			const removable = this.props.appointment.map(a => a.dateTime.isAfter(moment())).getOrElse(false);
			actions.push(StreamItemAction.new("Edit", this.editAppointmentClick));
			if (this.isCurrent()) {
				actions.push(this.isConfirmed() ?
					StreamItemAction.new("Unconfirm", this.unConfirmAppointmentClick) :
					StreamItemAction.new("Confirm", this.confirmAppointmentClick))
			}
			if (removable) {
				actions.push(StreamItemAction.new("Delete", this.deleteAppointmentClick))
			}
		}
		return actions;
	}

	isConfirmed() {
		return this.props.appointment.map(a => a.status.value === AppointmentStatusEnum.Confirmed).getOrElse(false);
	}

	isCurrent() {
		return this.props.activity.data.appointmentDate.isAfter(moment());
	}

	editAppointmentClick() {
		this.props.onEditAppointmentClick(this.props.activity.data.id);
	}

	confirmAppointmentClick() {
		this.props.appointment.map((appointment) => {
			if (this.props.contact.map(c => c.isOneOfPhoneNumbersMobile()).getOrElse(false)) {
				this.setState({confirmationVisible: true});
			}
			else {
				this.props.updateAppointment(this.props.activity.jobId,
					appointment.copy({status: AppointmentStatus.fromString(AppointmentStatusEnum.Confirmed)}));
			}
		});
	}

	unConfirmAppointmentClick() {
		this.props.appointment.map((appointment) => {
			this.props.updateAppointment(this.props.activity.jobId,
				appointment.copy({status: AppointmentStatus.fromString(AppointmentStatusEnum.Unconfirmed)}));
		});
	}

	onModalConfirm() {
		this.setState({confirmationVisible: false}, () => {
			this.props.appointment.map((appointment) => {
				this.props.updateAppointment(this.props.activity.jobId,
					appointment.copy({status: AppointmentStatus.fromString(AppointmentStatusEnum.Confirmed)}));
			});
		});
	}

	deleteAppointmentClick() {
		this.props.appointment.map(appointment => {
			this.setState({deleteAppointmentConfirmationVisible: true});
		});
	}

	deleteAppointment() {
		this.props.appointment.map(a => this.props.deleteAppointment(a.id,
			() => this.setState({deleteAppointmentConfirmationVisible: false})
		))
	}
}

function mapStateToProps(state: RootState, props: ComponentProps) {
	const job = getJobByIdOrRefNumber(props.activity.jobId, state);
	const appointment = state.appointment.jobAppointments.getOrElse([]).filter(
		(a: JobAppointment) => a.id === props.activity.data.id);
	return ({
		contact: job.map(j => j.data.principalContactId).flatMap(getIndividualContact),
		appointment: option(appointment[0])
	})
}


function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		updateAppointment: (jobId: string, appointment: JobAppointment) => dispatch(
			updateJobAppointment(jobId, appointment,
				() => {
					Toast.success("Appointment has been updated.", "Update Appointment");
				},
				(error: any) => {
					let err: string;
					if (error && error.response &&
						error.response.data.exception == "JobAppointmentRequiredAssigneeToConfirmException")
						err = `Cannot confirm appointment without assignee.`;
					else
						err = "Sorry, something went wrong. Try again later.";
					Toast.error(err, "Update Appointment");
				})),
		deleteAppointment: (appointmentId: string, onSuccess: OnSuccess) => dispatch(deleteJobAppointment(appointmentId,
			() => {
				Toast.success("Appointment has been deleted", "Delete appointment");
				onSuccess()
			},
			() => {Toast.error("Something went wrong. Please try again later.", "Delete appointment")}))
	}
}

export default withRouter<ComponentProps>(connect(mapStateToProps, mapDispatchToProps)(JobActivityAppointmentItem));